import { createContext } from "preact";
import { useContext } from "preact/hooks";
import type { createOutbox } from "../../live/outbox";
import { uuid } from "../../live/uuid";
import { type Coin, consolidate, type Purse, purseDeltas } from "./coins";
import { type InventoryOp, quantityAttrs, reorderOps } from "./items";
import type { InventoryView, SheetView } from "./sheet_view";
import type { Changes } from "./spell_picker";
import { xpTotal } from "./xp";

type SheetOp = { op: "upsert"; collection: "sheet"; attrs: Record<string, unknown> };
// The campaign's coins, a record of its own in the hub's state.
type PurseOp = { op: "upsert"; collection: "purse"; attrs: Record<string, unknown> };
type Op = SheetOp | PurseOp | InventoryOp;
type Writer = Pick<ReturnType<typeof createOutbox>, "enqueue">;

// The writes the sheet makes, each sent with the same change applied at once to what the player sees.
export type Edits = ReturnType<typeof createEdits>;

export const EditContext = createContext<Edits | null>(null);

// The coin and item writes alone, which the campaign's inventory has too; it provides them here, a sheet through EditContext.
export type InventoryEdits = ReturnType<typeof createInventoryEdits>;

export const InventoryEditContext = createContext<InventoryEdits | null>(null);

export function useInventoryEdits(): InventoryEdits | null {
	const own = useContext(InventoryEditContext);
	const sheet = useContext(EditContext);
	return own ?? sheet;
}

export const clampHealth = (value: number, max: number) => Math.max(0, Math.min(max, value));

// The fields a write changes that the load is built from: the coins, and an item's quantity.
export const LOAD_KEYS = ["money_gp", "money_sp", "money_cp", "quantity"];

export function touches(pending: { ops: { attrs?: Record<string, unknown> }[] }[], keys: string[]): boolean {
	return pending.some((entry) => entry.ops.some((op) => keys.some((key) => op.attrs && key in op.attrs)));
}

type Outcome = { mutationId: string };

// Who is waiting for a write's answer: the outbox reports each accepted write as settled and each refused one as rejected.
// A replayed one, or one answered across a version gap, is settled before the sheet shows what it did, so its sheet is
// not yet the server's.
export function createOutcomes() {
	const waiting = new Map<string, (taken: boolean) => void>();
	const replays = new Set<string>();
	const finish = (taken: boolean) => (outcome: Outcome) => {
		waiting.get(outcome.mutationId)?.(taken);
		waiting.delete(outcome.mutationId);
	};
	return {
		wait: (mutationId: string) => new Promise<boolean>((resolve) => waiting.set(mutationId, resolve)),
		settled: ({ mutationId, applied = true }: Outcome & { applied?: boolean }) => {
			if (!applied && waiting.has(mutationId)) replays.add(mutationId);
			finish(true)({ mutationId });
		},
		rejected: finish(false),
		replayed: ({ mutationId }: Outcome) => {
			if (waiting.has(mutationId)) replays.add(mutationId);
		},
		wasReplayed: (mutationId: string) => replays.delete(mutationId),
	};
}

type Deps = {
	outbox: Writer;
	outcomes: Pick<ReturnType<typeof createOutcomes>, "wait" | "wasReplayed">;
	url: string;
	refuse: () => void;
};

// A write whose result the caller waits for: whether the server took it. What the owner cannot foresee comes back in the
// answer.
export const asking =
	({ outbox, outcomes, url }: Deps) =>
	(body: object, ops: Op[] = [], mutationId = uuid()): Promise<boolean> => {
		const taken = outcomes.wait(mutationId);
		outbox.enqueue({ url, body, ops, mutationId });
		return taken;
	};

// The coin and item writes, each sent with the same change applied at once. `purse` wraps a change of the coins in the op
// that moves them in the owner's state.
export function createInventoryEdits(deps: Deps, view: () => InventoryView | null, purse: (attrs: Record<string, unknown>) => Op) {
	const { outbox, url, refuse } = deps;
	const ask = asking(deps);
	const inventory = (id: string, attrs: Record<string, unknown>): InventoryOp[] => [{ op: "update", collection: "inventory", id, attrs }];
	const coins = (coin: Coin, delta: number) => {
		const attribute = `money_${coin}` as const;
		const next = (view()?.sheet[attribute] ?? 0) + delta;
		if (next >= 0) outbox.enqueue({ url, body: { op: "coins", [coin]: delta }, ops: [purse({ [attribute]: next })] });
		else refuse();
	};
	return {
		// The entry is built on the server, and priced there unless the player negotiated a `cost` (copper, the total);
		// its item and the coins come back in the answer. Resolves to whether the server took it.
		addCatalogItem: (slug: string, quantity: number, pay: boolean, cost?: number) =>
			ask({ op: "item_create", from: slug, quantity, pay, ...(cost === undefined ? {} : { cost }), client_id: uuid() }),
		createItem: (item: Record<string, unknown>) => ask({ op: "item_create", item, client_id: uuid() }),
		editItem: (id: string, attrs: Record<string, unknown>) => ask({ op: "item_update", id, attrs }),
		setEquipped(id: string, equipped: boolean) {
			outbox.enqueue({ url, body: { op: "item_update", id, attrs: { equipped } }, ops: inventory(id, { equipped }) });
		},
		setQuantity(id: string, quantity: number) {
			const item = view()?.inventory.find((entry) => entry.id === id);
			if (!item || quantity < 1 || quantity === item.quantity) return;
			outbox.enqueue({ url, body: { op: "item_update", id, attrs: { quantity } }, ops: inventory(id, quantityAttrs(item, quantity)) });
		},
		reorder: (ids: string[]) => outbox.enqueue({ url, body: { op: "item_reorder", ids }, ops: reorderOps(ids) }),
		destroyItem: (id: string) => outbox.enqueue({ url, body: { op: "item_destroy", id }, ops: [{ op: "remove", collection: "inventory", id }] }),
		coins,
		// A typed coin is a delta from what the field started at, so a purse spent from elsewhere meanwhile still lands.
		setCoin(coin: Coin, next: number, from: number) {
			if (next !== from) coins(coin, next - from);
		},
		convertCoins() {
			const owner = view()?.sheet;
			if (!owner) return;
			const current: Purse = { gp: owner.money_gp, sp: owner.money_sp, cp: owner.money_cp };
			const target = consolidate(current);
			const deltas = purseDeltas(current, target);
			if (Object.keys(deltas).length === 0) return;
			outbox.enqueue({ url, body: { op: "coins", ...deltas }, ops: [purse({ money_gp: target.gp, money_sp: target.sp, money_cp: target.cp })] });
		},
	};
}

export function createEdits(deps: Deps & { view: () => SheetView | null }) {
	const { outbox, outcomes, url, view } = deps;
	const send = (body: object, attrs: Record<string, unknown>) => {
		const ops: SheetOp[] = [{ op: "upsert", collection: "sheet", attrs }];
		outbox.enqueue({ url, body, ops });
	};
	const ask = asking(deps);
	const setFields = (fields: Record<string, unknown>) => send({ op: "set_fields", fields }, fields);

	return {
		...createInventoryEdits(deps, view, (attrs) => ({ op: "upsert", collection: "sheet", attrs })),
		setHealth(value: number, max: number) {
			const next = clampHealth(value, max);
			if (next !== view()?.sheet.health_points) setFields({ health_points: next });
		},
		// A typed "-7" or "+3": the server adds it to the PV it holds, so changes made at the same time all land. It is always
		// sent, whatever the sheet shows: the bounds only shape the optimistic display.
		changeHealth(delta: number, max: number) {
			const current = view()?.sheet.health_points;
			if (current === undefined || delta === 0) return;
			send({ op: "change_health", delta }, { health_points: clampHealth(current + delta, max) });
		},
		setDead: (dead: boolean) => setFields({ dead }),
		setHeroicAction: (used: boolean) => setFields({ heroic_action_used: used }),
		setNotes: (notes: string) => setFields({ notes }),
		toggleUse(key: string, used: boolean) {
			const spent = (view()?.sheet.resource_uses ?? []).filter((entry) => entry !== key);
			send({ op: "toggle_use", key, used }, { resource_uses: used ? [...spent, key] : spent });
		},
		// A negative delta is a correction. The XP move at once; the rest of the box is the server's.
		// Resolves to whether the server took it.
		addXp(delta: number): Promise<boolean> {
			const experience = view()?.sheet.experience;
			if (!experience || delta === 0) return Promise.resolve(false);
			const ops: SheetOp[] = [{ op: "upsert", collection: "sheet", attrs: { experience: { ...experience, points: xpTotal(experience.points, delta) } } }];
			return ask({ op: "add_xp", amount: delta }, ops);
		},
		// The server builds what a level changes, so nothing is foreseen. Resolves to the write's mutation id (the undo's token) once the
		// server took it, null when it refused.
		async levelUp(body: object): Promise<string | null> {
			const mutationId = uuid();
			return (await ask(body, [], mutationId)) ? mutationId : null;
		},
		undoLevelUp: (token: string) => ask({ op: "undo_level_up", token }),
		levelChoices: (body: object) => ask(body),
		moveItem: (id: string, remove: boolean) => ask({ op: "item_to_campaign", id, remove }, remove ? [{ op: "remove", collection: "inventory", id }] : []),
		// Today's rest: `healing` null leaves the natural roll to the server. What the sheet can foresee moves at once. With
		// spell changes (`prepare`) it is one write that the server takes whole or refuses whole, and resolves to which; the
		// new spells come back in the answer.
		rest(healing: number | null, resetDailies: boolean, prepare?: { ability_id: string; changes: Changes }): Promise<boolean> {
			const current = view();
			if (!current) return Promise.resolve(false);
			const attrs: Record<string, unknown> = {};
			if (healing !== null) attrs.health_points = clampHealth(current.sheet.health_points + healing, current.derived.max_hp.total);
			if (resetDailies && current.record_type === "character") attrs.resource_uses = [];
			const ops: SheetOp[] = Object.keys(attrs).length ? [{ op: "upsert", collection: "sheet", attrs }] : [];
			const body = { op: "rest", healing, reset_dailies: resetDailies };
			if (prepare) return ask({ ...body, prepare }, ops);
			outbox.enqueue({ url, body, ops });
			return Promise.resolve(true);
		},
		// The spells a character memorizes and writes in the grimório: only the keys that changed (see `diff`). Resolves to
		// whether the server took it; the rows come back in the answer.
		prepareSpells: (abilityId: string, changes: Changes) => ask({ op: "prepare_spells", ability_id: abilityId, changes }),
		// The d20 was rolled here; the server judges it against the target, and marks the character dead on a failure.
		// Resolves to whether it lived, from the sheet the server's answer left. A replay (the first answer was lost) says
		// nothing yet, and neither does an answer across a version gap, so both resolve false and the band shows what
		// happened after the resync.
		async agonize(roll: number, adjustment: number): Promise<boolean> {
			const mutationId = uuid();
			const taken = outcomes.wait(mutationId);
			outbox.enqueue({ url, body: { op: "agonize", roll, adjustment }, ops: [], mutationId });
			const settled = await taken;
			return !outcomes.wasReplayed(mutationId) && settled && view()?.sheet.dead === false;
		},
	};
}
